import { useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Link as MuiLink, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis,
} from 'recharts';
import { usePageTitle } from '../../components/shell/AppShell';
import { useRevenueMachine } from '../../api/revenue';
import type { RevenueMachine, TrendPoint } from '../../api/revenue';
import { fmtBool, fmtCurrencyCents, fmtDate, fmtDateTime, fmtOrEmDash, fmtPercent } from '../../utils/format';
import { PeriodSelector } from './PeriodSelector';
import type { PeriodValue } from './PeriodSelector';
import { completenessChip, dataStateChip, netWinColor, priorPeriodLabel, revenueDelta } from './revenueFormat';
import { KpiCard } from './KpiCard';
import { renderSuspectDot, SuspectTrendCaption } from './TrendSuspectDot';
import { SuspectDaysDialog } from './SuspectDaysDialog';
import { useRevenueExport } from './useRevenueExport';
import { RevenueRefreshButton } from './RevenueRefreshButton';

/**
 * Machine-level revenue drill-down (task 11 — UP-MVP wave 1). Mirrors the
 * Portfolio/Site pages' structure (KPI cards + trend chart, hand-rolled
 * MUI components, no RouteDataGrid — that choice is parked for owner
 * decision, see RevenueSitePage) but there is no child table: a machine is
 * the leaf of the drill-down, so the page shows identity, the period meter
 * components, and the machine's own completeness/connectivity state.
 *
 * Two spec-mandated gates (kept as inline conditionals, not separate
 * components, so the gating logic stays visible at the call site):
 *   - F-4 / UP-MVP-040 §5.2: current connectivity (`onlineNow`/`stale`) +
 *     `lastSnapshotAt` render ONLY when `dataState.currentPeriod` — these
 *     are *live* signals and must never sit unlabelled next to a
 *     historical revenue window.
 *   - F-11: the trend chart is hidden for `trend.length <= 1` (Today /
 *     Yesterday are single-gaming-day periods) — never render an
 *     intraday/empty chart.
 *
 * `useRevenueMachine` resolves to `null` on a 404 / out-of-scope machine
 * (never throws) — rendered as an explicit not-found state, never a crash
 * and never another machine's data.
 *
 * Export (Task 7 server-side endpoints, wired via `useRevenueExport`) sits
 * next to the period picker, same as the other two revenue pages.
 */
export default function RevenueMachinePage() {
  const { machineId } = useParams<{ machineId: string }>();
  const navigate = useNavigate();
  // Config drill: ?versionId scopes the page to ONE game version (config) of
  // this cabinet; absent = the current config. Configs are separate reporting
  // machines — nothing on this page ever sums across them.
  const [searchParams, setSearchParams] = useSearchParams();
  const versionId = searchParams.get('versionId');
  const download = useRevenueExport();

  const [period, setPeriod] = useState<PeriodValue>({ token: 'today' });
  const [reviewOpen, setReviewOpen] = useState(false);
  const q = useRevenueMachine(machineId ?? null, period.token, period.from, period.to, versionId);
  const machine = q.data;
  const notFound = !q.isLoading && !q.isError && machine === null;

  usePageTitle(machine ? machine.machineLabel : 'Revenue');

  const handleBack = () => {
    if (machine) navigate(`/revenue/sites/${machine.siteId}`);
    else navigate('/revenue');
  };

  return (
    <Stack spacing={2.5}>
      <Button onClick={handleBack} sx={{ alignSelf: 'flex-start' }}>
        ← Back to site
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <PeriodSelector value={period} onChange={setPeriod} />
        <Stack direction="row" spacing={1}>
          <RevenueRefreshButton isFetching={q.isFetching} onRefresh={() => { void q.refetch(); }} />
          <Button
            size="small" variant="outlined" disabled={!machine}
            onClick={() => download('machines', machineId ?? null, period.token, period.from, period.to, 'csv')}
          >
            Export CSV
          </Button>
          <Button
            size="small" variant="outlined" disabled={!machine}
            onClick={() => download('machines', machineId ?? null, period.token, period.from, period.to, 'pdf')}
          >
            Export PDF
          </Button>
        </Stack>
      </Stack>

      {q.isLoading && (
        <Typography variant="body2" color="text.secondary">Loading revenue…</Typography>
      )}
      {q.isError && (
        <Alert severity="error">
          {(q.error as { message?: string })?.message ?? 'Failed to load revenue data.'}
        </Alert>
      )}
      {notFound && (
        <Alert severity="warning">
          Machine not found, or you don&apos;t have access to it.
        </Alert>
      )}

      {machine && (
        <>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{machine.machineLabel}</Typography>
            <CompletenessChip completeness={machine.completeness} />
            {machine.metersStale && <NoMeterMovementChip />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            <MuiLink component={RouterLink} to={`/revenue/sites/${machine.siteId}`}>{machine.siteName}</MuiLink>
            {machine.gameName ? ` · ${machine.gameName}` : ''}
            {machine.versionNo != null ? ` · config v${machine.versionNo}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">{machine.scopeDescription}</Typography>

          {machine.dataState.provisional && (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>Provisional data</AlertTitle>
              <Typography variant="body2">
                {machine.dataState.currentPeriod
                  ? 'This period includes today, which is still being collected and may change.'
                  : 'This machine has one or more suspect readings in the selected period — included in the totals but flagged for review (see the red markers on the trend).'}
              </Typography>
              {machine.dataState.affectedCount > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="text" color="warning"
                    onClick={() => setReviewOpen(true)}
                    sx={{ textTransform: 'none', p: 0, textAlign: 'left' }}
                  >
                    Review suspect meter-days →
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          {reviewOpen && (
            <SuspectDaysDialog
              open
              onClose={() => setReviewOpen(false)}
              period={period.token}
              from={period.from}
              to={period.to}
              machineId={machine.machineId}
              title={`Suspect meter-days · ${machine.machineLabel}`}
            />
          )}

          <DetailsRow machine={machine} />

          <KpiRow machine={machine} />

          {/* F-4 / UP-MVP-040 — current-period only. */}
          {machine.dataState.currentPeriod && <ConnectivityRow machine={machine} />}

          {machine.trend.length > 1 && <TrendChart trend={machine.trend} />}

          {machine.versions.length > 1 && (
            <VersionsPanel
              machine={machine}
              activeVersionId={machine.versionId}
              onSelect={(vid) => setSearchParams(vid ? { versionId: vid } : {})}
            />
          )}
        </>
      )}
    </Stack>
  );
}

function CompletenessChip({ completeness }: { completeness: RevenueMachine['completeness'] }) {
  const chip = completenessChip(completeness);
  return <Chip size="small" label={chip.label} color={chip.color} />;
}

/**
 * RM "no meter movement" detector (defense-in-depth, informational — see
 * MeterIngestProcessor). Deliberately worded as an observation, not a
 * diagnosis: the RM cannot distinguish a genuinely idle machine from a
 * wedged gateway by meter values alone, so this reads "no meter movement",
 * never "gateway wedged" / "machine down". Warning tone (not error) — same
 * severity class as completenessChip's 'incomplete'.
 */
function NoMeterMovementChip() {
  return <Chip size="small" label="No meter movement 10m+" color="warning" />;
}

const TURNOVER_COLOR = '#1976d2';
const NETWIN_COLOR = '#2e7d32';

function DetailsRow({ machine }: { machine: RevenueMachine }) {
  const rtpPct = machine.rtpBasisPoints != null ? machine.rtpBasisPoints / 100 : null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
      <KpiCard label="Game" value={fmtOrEmDash(machine.gameName)} />
      <KpiCard label="Denomination" value={fmtCurrencyCents(machine.denominationCents)} />
      <KpiCard label="RTP" value={fmtPercent(rtpPct)} />
    </Box>
  );
}

function KpiRow({ machine }: { machine: RevenueMachine }) {
  const { totals, priorTotals, dataState, trend, periodToken } = machine;
  const chip = dataStateChip(dataState);
  const priorLabel = priorPeriodLabel(periodToken);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2 }}>
      <KpiCard
        label="Total in"
        value={fmtCurrencyCents(totals.turnoverCents)}
        showDelta
        delta={revenueDelta(totals.turnoverCents, priorTotals?.turnoverCents ?? null)}
        deltaLabel={priorLabel}
        sparkline={trend.map((t) => t.turnoverCents)}
        sparklineColor={TURNOVER_COLOR}
      />
      <KpiCard label="Total out" value={fmtCurrencyCents(totals.totalOutCents)} />
      <KpiCard label="Jackpots" value={fmtCurrencyCents(totals.jackpotsCents)} />
      <KpiCard
        label="Net machine win"
        value={fmtCurrencyCents(totals.netWinCents)}
        valueColor={netWinColor(totals.netWinCents)}
        secondary={`Hold ${fmtPercent(totals.holdPct)}`}
        showDelta
        delta={revenueDelta(totals.netWinCents, priorTotals?.netWinCents ?? null)}
        deltaLabel={priorLabel}
        sparkline={trend.map((t) => t.netWinCents)}
        sparklineColor={NETWIN_COLOR}
      />
      <KpiCard label="Data health" value={chip.label} />
    </Box>
  );
}

function ConnectivityRow({ machine }: { machine: RevenueMachine }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
      <KpiCard label="Online now" value={fmtBool(machine.onlineNow)} />
      <KpiCard label="Stale" value={fmtBool(machine.stale)} />
      <KpiCard label="Last snapshot" value={fmtDateTime(machine.lastSnapshotAt)} />
    </Box>
  );
}

function TrendChart({ trend }: { trend: TrendPoint[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Turnover vs net win</Typography>
      <Box data-testid="revenue-machine-trend-chart" sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="gamingDay" tickFormatter={(v) => fmtDate(v)} minTickGap={30} />
            {/* Net win on its own right-hand axis — only a few % of turnover
                (the hold), so a shared scale flattens it. Axes coloured to match
                their lines. */}
            <YAxis yAxisId="turnover" tickFormatter={(v) => fmtCurrencyCents(v)} width={90} stroke="#1976d2" />
            <YAxis yAxisId="netwin" orientation="right" tickFormatter={(v) => fmtCurrencyCents(v)} width={90} stroke="#2e7d32" />
            <RechartsTooltip
              labelFormatter={(v) => fmtDate(v)}
              formatter={(value) => fmtCurrencyCents(Number(value))}
            />
            <Legend />
            {/* dot renders a red ring only on suspect gaming days (included but
                flagged for review); all other points draw nothing. */}
            <Line yAxisId="turnover" type="monotone" dataKey="turnoverCents" name="Turnover" stroke="#1976d2" strokeWidth={2} dot={renderSuspectDot} activeDot={{ r: 4 }} />
            <Line yAxisId="netwin" type="monotone" dataKey="netWinCents" name="Net win" stroke="#2e7d32" strokeWidth={2} dot={renderSuspectDot} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <SuspectTrendCaption />
    </Paper>
  );
}

/**
 * The cabinet's configs (game versions) with data in the window — one row per
 * config, each its OWN totals. A config change (e.g. after a RAM reset) is a
 * new reporting machine: rows are never summed together. Clicking a row
 * re-scopes the page to that config.
 */
function VersionsPanel({
  machine, activeVersionId, onSelect,
}: {
  machine: RevenueMachine;
  activeVersionId: string | null;
  onSelect: (versionId: string | null) => void;
}) {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Machine configs in this period
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Config</TableCell>
              <TableCell align="right">Net win</TableCell>
              <TableCell align="right">Turnover</TableCell>
              <TableCell align="right">Total out</TableCell>
              <TableCell align="right">Hold</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {machine.versions.map((v) => (
              <TableRow
                key={v.versionId}
                hover
                selected={v.versionId === activeVersionId}
                sx={{ cursor: 'pointer' }}
                onClick={() => onSelect(v.versionId)}
              >
                <TableCell>{v.gameName ?? machine.machineLabel} (v{v.versionNo})</TableCell>
                <TableCell align="right">{fmtCurrencyCents(v.netWinCents)}</TableCell>
                <TableCell align="right">{fmtCurrencyCents(v.turnoverCents)}</TableCell>
                <TableCell align="right">{fmtCurrencyCents(v.totalOutCents)}</TableCell>
                <TableCell align="right">{fmtPercent(v.holdPct)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
